/*
 * Where an autocomplete is happening: the token under the caret starts with a trigger character
 * and stands alone (line start or whitespace before it), and its term has reached the source's
 * minimum. A source anchored at the start — a command palette — opens only while its trigger is the
 * first character of the whole message. Pure, so the combobox's opening rule is a table test.
 */

import type { AutocompleteSource } from "../types.ts"

export interface ActiveTrigger {
	source: AutocompleteSource
	/** Offset of the trigger character; the replacement starts here. */
	start: number
	term: string
}

export const MAX_ITEMS = 8

/**
 * @param startsMessage whether offset 0 of `text` is the start of the message. The textarea hands
 * in the whole message and leaves it true; the formatted editor hands in one block's text and says
 * whether that block is the message's first.
 */
export function findTrigger(
	text: string,
	caret: number,
	sources: readonly AutocompleteSource[],
	startsMessage = true,
): ActiveTrigger | null {
	let start = caret
	while (start > 0 && !/\s/.test(text.charAt(start - 1))) start--
	const token = text.slice(start, caret)
	if (token === "") return null
	const atStart = start === 0 && startsMessage
	const source = sources.find(
		(s) => token.startsWith(s.trigger) && (s.anchor !== "start" || atStart),
	)
	if (source === undefined) return null
	const term = token.slice(source.trigger.length)
	return term.length >= source.minChars ? { source, start, term } : null
}
